import { PaymentAmounts } from 'common/modules/atoms/paymentAmounts';
import { OrderProduct, PartialWithId } from 'common/types';
import { StatusWithVariant } from '../utils';

export const getUpdatedProductsWithNewDepositValue = (
	products: OrderProduct[],
	newDepositValue: number,
): PartialWithId<OrderProduct>[] => {
	const productsWithDeposit = products.filter((p) => p.pricing.deposit != null);
	const totalOldDeposit = productsWithDeposit
		.map((p) => p.pricing.deposit || 0)
		.reduce((a, b) => a + b, 0);
	let newDepositValueLeft = newDepositValue;
	return productsWithDeposit.map((p, i) => {
		const productDeposit = p.pricing.deposit || 0;
		const productDepositRatio =
			totalOldDeposit === 0 ? 1 / productsWithDeposit.length : productDeposit / totalOldDeposit;
		let newProductDeposit = newDepositValue * productDepositRatio;
		newDepositValueLeft -= newProductDeposit;
		if (i === productsWithDeposit.length) {
			newProductDeposit += newDepositValueLeft;
		}
		return {
			id: p.id,
			data: {
				pricing: {
					...p.pricing,
					deposit: newProductDeposit,
				},
			},
		};
	});
};

export type DepositStatus =
	| 'charged'
	| 'unreserved'
	| 'reserved'
	| 'refunded'
	| 'cancelled'
	| 'pending_capture'
	| 'pending_refund';

export const getDepositStatus = (
	paymentAmounts: PaymentAmounts,
): StatusWithVariant<DepositStatus> => {
	const depositStatusOrder: StatusWithVariant<DepositStatus>[] = [
		{ status: 'charged', variant: 'warning' },
		{ status: 'pending_capture', variant: 'neutral' },
		{ status: 'pending_refund', variant: 'neutral' },
		{ status: 'reserved', variant: 'success' },
		{ status: 'cancelled', variant: 'neutral' },
		{ status: 'refunded', variant: 'warning' },
		{ status: 'unreserved', variant: 'danger' },
	];
	for (const statusWithColor of depositStatusOrder) {
		const { status } = statusWithColor;
		if (hasDepositStatus(status, paymentAmounts)) {
			return statusWithColor;
		}
	}
	return { status: 'unreserved', variant: 'danger' };
};

const hasDepositStatus = (status: DepositStatus, paymentAmounts: PaymentAmounts) => {
	switch (status) {
		case 'cancelled':
			return paymentAmounts.cancelled > 0;
		case 'charged':
			return paymentAmounts.paid > 0;
		case 'unreserved':
			return paymentAmounts.unreserved > 0;
		case 'reserved':
			return paymentAmounts.unreserved <= 0;
		case 'refunded':
			return paymentAmounts.refunded > 0;
		case 'pending_capture':
			return paymentAmounts.pendingCaptures > 0;
		case 'pending_refund':
			return paymentAmounts.pendingRefunds > 0;
	}
};
